import { useId, type SVGAttributes } from "react";

import { cn } from "@trip/utils";

function Oval({
  fillPercentage,
  ...props
}: { fillPercentage: number } & SVGAttributes<SVGSVGElement>) {
  const id = useId();
  const fillColor = "#00AA6C";
  const clipWidth =
    fillPercentage === 0.5 ? "9" : fillPercentage === 1 ? "18" : "0";

  return (
    <svg width="18" height="18" viewBox="0 0 18 18" {...props}>
      <defs>
        <clipPath id={id}>
          <rect x="0" y="0" width={clipWidth} height="18" />
        </clipPath>
      </defs>
      <ellipse
        cx="9"
        cy="9"
        rx="8"
        ry="8"
        stroke="#00AA6C"
        fill="white"
        strokeWidth="1.5"
      />
      <ellipse
        cx="9"
        cy="9"
        rx="8"
        ry="8"
        fill={fillColor}
        clipPath={`url(#${id})`}
      />
    </svg>
  );
}

export function TripadvisorRating({
  value,
  size = "default",
  owl = true,
}: {
  owl?: boolean;
  size?: "default" | "small";
  value: number;
}) {
  if (value < 0.0 || value > 5.0) {
    throw new Error("Value must be between 0.0 and 5.0");
  }

  const fullOvals = Math.floor(value);
  const halfOval = value % 1 >= 0.5 ? 1 : 0;
  const emptyOvals = 5 - fullOvals - halfOval;

  return (
    <div className="flex items-center gap-px *:shrink-0">
      {owl ? (
        <svg
          width="32"
          height="20"
          viewBox="0 0 32 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cn("mr-1", size === "small" ? "h-[10px] w-[16px]" : "")}
        >
          <path
            d="M7.79013 14.3384C8.33624 14.3384 8.8701 14.1766 9.32419 13.8732C9.77829 13.5699 10.1322 13.1387 10.3413 12.6341C10.5503 12.1296 10.605 11.5745 10.4985 11.0388C10.3919 10.5032 10.129 10.0112 9.74282 9.62508C9.35667 9.23893 8.86468 8.97596 8.32906 8.86944C7.79345 8.76292 7.23828 8.81763 6.73376 9.02665C6.22924 9.23567 5.79804 9.58961 5.49469 10.0437C5.19134 10.4978 5.02947 11.0317 5.02954 11.5778C5.02964 12.3099 5.32052 13.012 5.83821 13.5297C6.3559 14.0474 7.05801 14.3383 7.79013 14.3384Z"
            fill="black"
          />
          <path
            d="M23.3547 14.3387C24.8796 14.3387 26.1157 13.1026 26.1157 11.5777C26.1157 10.0529 24.8796 8.81677 23.3547 8.81677C21.8299 8.81677 20.5938 10.0529 20.5938 11.5777C20.5938 13.1026 21.8299 14.3387 23.3547 14.3387Z"
            fill="black"
          />
          <path
            d="M0.00805982 11.5778C0.00805982 15.8762 3.49209 19.3598 7.79014 19.3598C9.75211 19.3625 11.6421 18.6213 13.0791 17.2855L15.5738 20L18.0684 17.287C19.5053 18.6217 21.3947 19.3623 23.3558 19.3595C27.6543 19.3595 31.1414 15.875 31.1414 11.5774C31.1425 10.4946 30.9171 9.42353 30.4797 8.43302C30.0423 7.44252 29.4025 6.55447 28.6015 5.82591L31.1479 3.05534H25.5028C22.5772 1.06458 19.1204 0 15.5818 0C12.0432 0 8.58642 1.06458 5.66088 3.05534H0L2.54644 5.82591C1.74562 6.55463 1.10609 7.4428 0.668935 8.43338C0.23178 9.42395 0.00666798 10.495 0.00805982 11.5778ZM28.6214 11.5778C28.6214 12.6194 28.3125 13.6377 27.7338 14.5038C27.1551 15.3699 26.3326 16.045 25.3702 16.4436C24.4078 16.8422 23.3489 16.9465 22.3272 16.7433C21.3056 16.5401 20.3671 16.0385 19.6306 15.3019C18.894 14.5653 18.3924 13.6269 18.1892 12.6052C17.9859 11.5836 18.0902 10.5246 18.4889 9.56226C18.8875 8.59989 19.5625 7.77734 20.4287 7.19863C21.2948 6.61991 22.313 6.31102 23.3547 6.31102C24.7515 6.31102 26.0911 6.86591 27.0788 7.85361C28.0665 8.84132 28.6214 10.1809 28.6214 11.5778ZM15.5726 2.54606C17.5799 2.54356 19.5676 2.94096 21.4196 3.71508C18.0956 4.98695 15.5753 7.96055 15.5753 11.4258C15.5753 7.96016 13.0542 4.98618 9.72981 3.71469C11.5805 2.94118 13.5667 2.54391 15.5726 2.54606ZM7.79014 6.31102C8.83181 6.31102 9.85007 6.61991 10.7162 7.19863C11.5823 7.77734 12.2573 8.59989 12.656 9.56226C13.0546 10.5246 13.1589 11.5836 12.9557 12.6052C12.7525 13.6269 12.2509 14.5653 11.5143 15.3019C10.7777 16.0385 9.83928 16.5401 8.81763 16.7433C7.79599 16.9465 6.73702 16.8422 5.77465 16.4436C4.81228 16.045 3.98973 15.3699 3.41102 14.5038C2.8323 13.6377 2.52341 12.6194 2.52341 11.5778C2.52341 10.1809 3.0783 8.84132 4.066 7.85361C5.05371 6.86591 6.39332 6.31102 7.79014 6.31102Z"
            fill="black"
          />
        </svg>
      ) : null}
      {Array(fullOvals)
        .fill(0)
        .map((oval: number, i) => (
          <Oval
            className={size === "small" ? "size-[9px]" : ""}
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={oval.toString() + i}
            fillPercentage={1}
          />
        ))}
      {halfOval > 0 ? (
        <Oval
          className={size === "small" ? "size-[9px]" : ""}
          fillPercentage={0.5}
        />
      ) : null}
      {Array(emptyOvals)
        .fill(0)
        .map((oval: number, i) => (
          <Oval
            className={size === "small" ? "size-[9px]" : ""}
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={oval + i + fullOvals + halfOval}
            fillPercentage={0}
          />
        ))}
    </div>
  );
}
